import { useBridge } from '@web/adapter/useBridgeRuntime'
import { api } from '@web/api/client'
import type { RelatedSession } from '@web/api/types'
import { Icon } from '@web/components/ui/icons'
import { navigateWorkspace, useIdentity } from '@web/state/identity'
import { type ReactNode, useEffect, useState } from 'react'

/** Session links only target authenticated host routes and accessible organizations. */
export function SessionLink({
  session,
  children,
  className,
  containerClassName,
}: {
  session: RelatedSession
  children?: ReactNode
  className?: string
  /** Classes for the wrapper, which owns an inline error message when navigation fails. */
  containerClassName?: string
}) {
  const identity = useIdentity()
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const path = `/apps/${encodeURIComponent(session.app_id)}/sessions/${encodeURIComponent(session.id)}`
  return (
    <span className={containerClassName}>
      <a
        className={className}
        href={path}
        aria-disabled={busy}
        onClick={async (event) => {
          const changesOrganization =
            session.org_id && session.org_id !== identity.state?.current_org?.id
          if (
            !changesOrganization &&
            (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
          )
            return
          event.preventDefault()
          if (busy) return
          setError('')
          setBusy(true)
          try {
            if (
              session.org_id &&
              session.org_id !== identity.state?.current_org?.id
            ) {
              if (
                !identity.state?.organizations.some(
                  (org) => org.id === session.org_id,
                )
              )
                throw new Error(
                  'This organization is not available to your account.',
                )
              await identity.switchOrganization(session.org_id)
            }
            navigateWorkspace(path)
          } catch (failure) {
            setError((failure as Error).message)
          } finally {
            setBusy(false)
          }
        }}
      >
        <Icon
          name={busy ? 'loader' : 'fork'}
          size={13}
          className={busy ? 'spin' : ''}
        />
        {children ?? session.title}
      </a>
      {error && (
        <span role="alert" className="text-danger text-xs">
          {error}
        </span>
      )}
    </span>
  )
}

export function SessionLinks({ sessionId }: { sessionId: string }) {
  const { snapshot } = useBridge()
  const [relations, setRelations] = useState<{
    children: RelatedSession[]
    parent_session?: RelatedSession | null
  }>({ children: [] })
  const [handoffs, setHandoffs] = useState<{
    outgoing: RelatedSession[]
    incoming: RelatedSession[]
  }>({ outgoing: [], incoming: [] })
  useEffect(() => {
    let active = true
    setRelations({ children: [] })
    setHandoffs({ outgoing: [], incoming: [] })
    void api
      .sessionChildren(sessionId)
      .then((value) => {
        if (active) setRelations(value)
      })
      .catch(() => {
        // Optional relationship chrome must not disrupt an otherwise accessible conversation.
      })
    void api
      .sessionHandoffs(sessionId)
      .then((value) => {
        if (active) setHandoffs(value)
      })
      .catch(() => {
        // The source and destination retain independent access checks.
      })
    return () => {
      active = false
    }
  }, [sessionId, snapshot.bridge.status, snapshot.acp.running])
  if (
    !relations.parent_session &&
    !relations.children.length &&
    !handoffs.outgoing.length &&
    !handoffs.incoming.length
  )
    return null
  return (
    <div className="related-sessions">
      {relations.parent_session && (
        <SessionLink session={relations.parent_session}>
          Parent session: {relations.parent_session.title}
        </SessionLink>
      )}
      {handoffs.incoming.map((session) => (
        <SessionLink key={session.id} session={session}>
          Source analysis: {session.title}
        </SessionLink>
      ))}
      {handoffs.outgoing.map((session) => (
        <SessionLink key={session.id} session={session}>
          Engineering handoff: {session.title}
        </SessionLink>
      ))}
      {!!relations.children.length && (
        <details>
          <summary>
            {relations.children.length}{' '}
            {relations.children.length === 1
              ? 'subagent session'
              : 'subagent sessions'}
          </summary>
          <div className="related-sessions-links">
            {relations.children.map((session) => (
              <SessionLink key={session.id} session={session} />
            ))}
          </div>
        </details>
      )}
    </div>
  )
}
